import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import AutoScrollBtn from '../AutoScrollBtn/AutoScrollBtn';

import Icon from '../../../../../components/common/Icon';
import RangeSlider from '../../../../../components/common/RangeSlider';
import AutoScrollSettingsModal from '../../../../../components/modals/AutoScrollSettingsModal/AutoScrollSettingsModal';
import { ReactComponent as ArrowBackIcon } from '../../../../../icons/arrow-left-circle-solid.svg';
import { ReactComponent as PlayIcon } from '../../../../../icons/play-solid.svg';
import { ReactComponent as SquareIcon } from '../../../../../icons/square.svg';

import useAutoScrollControls from '../../../../../hooks/useAutoScrollControls';
import { useGlobalContext } from '../../../../../context/GlobalContext';
import {
  SPEED_LEVELS,
  DEFAULT_SCROLL_SPEED,
  PACE_MAX,
  PACE_MIN,
  PACE_STEP,
} from '../../../../../utils/autoScrollConstants';
import { startScroll, stopScroll } from '../../../../../utils/autoScrollHelpers';
import { getScrollSpeedForSong } from '../../../../../utils/scrollSpeedPreferences';
import { getSetting, settingKeys } from '../../../../../utils/settings';

import './AutoScrollPlugin.scss';

const AutoScrollPlugin = ({ mirror, compact, song }) => {
  const { t } = useTranslation();
  const { scrollTimerRef, isScrolling, setIsScrolling, setScrollingSpeed } = useGlobalContext();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Whether the pace button has opened into its slider. Closed is the resting state: the row is a
  // song's controls, and the pace is set once and then left alone.
  const [paceOpen, setPaceOpen] = useState(false);
  const paceRef = useRef(null);
  const controls = useAutoScrollControls(song);

  // Load saved scroll speed preference for this song (or reset to default if none saved)
  useEffect(() => {
    const rememberSpeedEnabled = getSetting(settingKeys.rememberScrollSpeed);
    const shouldRemember = rememberSpeedEnabled !== undefined ? rememberSpeedEnabled : true; // Default to true

    const savedSpeed = song?._id && shouldRemember ? getScrollSpeedForSong(song._id) : null;
    const speedToUse = savedSpeed !== null ? savedSpeed : DEFAULT_SCROLL_SPEED;

    setScrollingSpeed(speedToUse);

    // If currently scrolling, restart at the new speed
    if (isScrolling) {
      startScroll(scrollTimerRef, setIsScrolling, setScrollingSpeed, speedToUse, () => {
        stopScroll(scrollTimerRef, setIsScrolling);
      });
    }
  }, [song?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The slider is a mode, and a mode needs a way out that isn't hunting for the button again: it
  // closes on the next touch that lands anywhere else, and on Escape. Pointerdown rather than
  // click, so a drag that starts outside closes it before it moves anything.
  useEffect(() => {
    if (!paceOpen) return undefined;

    const closeOnOutside = (event) => {
      if (!paceRef.current?.contains(event.target)) setPaceOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setPaceOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [paceOpen]);

  // Constant mode has no pace at all, so an open slider has to go with the mode that owned it —
  // otherwise switching Smart Scroll off and back on returns to a row left mid-adjustment
  useEffect(() => {
    if (!controls.smart) setPaceOpen(false);
  }, [controls.smart]);

  // Compact view for service bar
  if (compact) {
    return (
      <div className="AutoScrollPlugin AutoScrollPlugin--compact">
        <span
          className={`compactBtn ${!controls.canSlowDown ? 'disabled' : ''}`}
          onClick={controls.canSlowDown ? controls.slowDown : undefined}
        >
          <Icon icon={<ArrowBackIcon />} className="previous-song-icon solid compactIconDown" />
        </span>
        <span
          className={`compactLabel ${controls.isScrolling ? 'active' : ''} ${controls.smart ? 'smart' : ''}`}
          onClick={controls.toggle}
          title={
            // The caret is the whole message at a glance; this is for anyone who hovers or uses a screen reader
            controls.smart
              ? t('view.smartScrollAt', { label: controls.label, context: controls.trend })
              : t('view.autoScrollSpeed', { label: controls.label })
          }
        >
          {controls.smart && (
            <span
              className={`trend trend--${controls.trend}`}
              style={{ '--trend-pulse': `${controls.trendPulse}ms` }}
              aria-hidden="true"
            />
          )}
          {controls.label}
        </span>
        <span
          className={`compactBtn ${!controls.canSpeedUp ? 'disabled' : ''}`}
          onClick={controls.canSpeedUp ? controls.speedUp : undefined}
        >
          <Icon icon={<ArrowBackIcon />} className="next-song-icon solid compactIconUp" />
        </span>
      </div>
    );
  }

  // Original view
  return (
    <>
      <div className={`song-plugin AutoScrollPlugin ${mirror && 'mirror horizontal-menu'}`}>
        {!mirror && (
          <span className="plugin__title clickable-link" onClick={() => setShowSettingsModal(true)}>
            {controls.smart ? t('view.smartScroll') : t('view.autoScroll')}
          </span>
        )}
        {controls.smart ? (
          // Smart scroll sets the velocity itself, so there are no levels to pick. The row becomes
          // start/stop plus the one calibration the schedule leaves to the player — the same
          // bordered box as the Metronome beside it, so it reads as the same kind of control
          <span className="plugin__controls">
            <span
              className={`btn-play-stop ${controls.isScrolling ? 'active' : ''}`}
              onClick={controls.toggle}
              title={controls.isScrolling ? t('view.stopAutoScroll') : t('view.startAutoScroll')}
            >
              <Icon icon={controls.isScrolling ? <SquareIcon /> : <PlayIcon />} />
            </span>
            {/* The pace is one number, and a -/+ pair around it spent two buttons and four taps to
                move it 20%. The number is the button instead, and it opens in place into a bar
                that spans the whole range — one drag from 50% to 200%, and the row goes back to
                reading as start/stop the moment you touch anything else. This is the only place
                pace is set now; the settings modal used to carry a second copy of the slider. */}
            <span className={`pace ${paceOpen ? 'open' : ''}`} ref={paceRef}>
              {paceOpen ? (
                <RangeSlider
                  // The plugin renders twice on the same page — the header row and the pinned
                  // bar's mirror — and a label points at an id, so the two can't share one
                  id={mirror ? 'auto-scroll-pace-mirror' : 'auto-scroll-pace'}
                  ariaLabel={t('view.smartScrollPace')}
                  label={controls.label}
                  value={controls.pace}
                  onChange={(event) => controls.changePace(Number(event.target.value))}
                  min={PACE_MIN}
                  max={PACE_MAX}
                  step={PACE_STEP}
                />
              ) : (
                <button
                  type="button"
                  className="pace-btn"
                  onClick={() => setPaceOpen(true)}
                  aria-expanded={false}
                  title={t('view.smartScrollPaceTitle')}
                >
                  {controls.label}
                </button>
              )}
            </span>
          </span>
        ) : (
          SPEED_LEVELS.map((level) => (
            <AutoScrollBtn
              key={level.speed}
              scrollAt={controls.scrollAtSpeed}
              speed={level.speed}
              label={level.label}
              selected={controls.isScrolling ? controls.scrollingSpeed : null}
              hasSavedPreference={controls.savedSpeed === level.speed}
            />
          ))
        )}
      </div>
      {/* The pace lives on the row now, as the bar the percentage opens into — a second copy in
          here would be one setting with two homes */}
      <AutoScrollSettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
    </>
  );
};

AutoScrollPlugin.propTypes = {
  mirror: PropTypes.bool,
  compact: PropTypes.bool,
  song: PropTypes.object,
};

export default AutoScrollPlugin;
